/**
 * Language Selector Component
 * 
 * A dropdown for selecting the user's preferred language for search results and UI.
 * The selected language is stored in localStorage and used for:
 * - Multilingual filtering and per-language relevance in search
 * - UI language via i18next, where a bundle exists
 *
 * The list covers every language the search index can analyze, which is wider
 * than the set of translated interfaces. Picking a language without a bundle
 * gives localized *results* with an English interface - i18next falls back on
 * its own, so no special handling is needed here.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import LanguageIcon from '@mui/icons-material/Language';
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  detectBrowserLanguage,
  normalizeLanguage,
  normalizeUiLanguage,
} from '../i18n/languages';

// Supported language codes
const LANGUAGE_CODES = SUPPORTED_LANGUAGES;

export const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

/**
 * Get the default language based on browser settings or fallback to 'en'
 */
function getDefaultLanguage(): string {
  // Check localStorage first
  const stored = normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  if (stored) {
    return stored;
  }

  // Fall back to the browser's preferences, then English
  return detectBrowserLanguage();
}

/**
 * Get the currently selected content language.
 *
 * This is what gets sent to the search API as `user_language`. It is normalized
 * so a stale or regional value in localStorage ('en-GB', or a language dropped
 * from SUPPORTED_LANGUAGES) cannot reach the backend, where it would match no
 * analyzer and silently fall back to the unstemmed catchall fields.
 */
export function getSelectedLanguage(): string {
  return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY)) ?? getDefaultLanguage();
}

interface LanguageSelectorProps {
  onChange?: (language: string) => void;
}

export function LanguageSelector({ onChange }: LanguageSelectorProps) {
  const { t, i18n } = useTranslation('common');
  const [language, setLanguage] = useState<string>(getDefaultLanguage);

  // Initialize language in localStorage if not set
  useEffect(() => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
  }, [language]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
    
    // Update i18next. Not every content language has a bundle, so map down to
    // one that does - the interface stays English for the rest.
    i18n.changeLanguage(normalizeUiLanguage(newLanguage) ?? DEFAULT_LANGUAGE);
    
    // Dispatch a custom event so other components can react (e.g., search re-execution)
    window.dispatchEvent(new CustomEvent('languageChange', { detail: newLanguage }));
    
    onChange?.(newLanguage);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FormControl size="small" variant="outlined">
        <Select
          value={language}
          onChange={handleChange}
          displayEmpty
          sx={{
            minWidth: 100,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          }}
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LanguageIcon fontSize="small" />
              <span>{language.toUpperCase()}</span>
            </Box>
          )}
        >
          {LANGUAGE_CODES.map((code) => (
            <MenuItem key={code} value={code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <span style={{ fontWeight: 500 }}>{t(`languages.${code}`)}</span>
                <span style={{ color: 'gray', marginLeft: 'auto' }}>({code})</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
